<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { usePermissions } from 'dashboard/composables/usePermissions';
import NextButton from 'dashboard/components-next/button/Button.vue';
import baileysChannel from 'dashboard/api/channel/baileysChannel';

const props = defineProps({
  inbox: { type: Object, required: true },
});

const { t } = useI18n();
const { hasPermission, isAdministrator } = usePermissions();
const canEdit = ref(
  isAdministrator.value || hasPermission('inbox.manage_session')
);

const identity = ref({});
const editingName = ref(false);
const editingStatus = ref(false);
const newName = ref('');
const newStatus = ref('');
const newPictureUrl = ref('');
const showPictureModal = ref(false);
const isLoading = ref({});

const fetchIdentity = async () => {
  try {
    const { data } = await baileysChannel.identity(props.inbox.id);
    identity.value = data;
    newName.value = data.name || '';
    newStatus.value = data.status || '';
  } catch (err) {
    // Sessao desconectada - nao polui UI
  }
};

const saveName = async () => {
  isLoading.value = { ...isLoading.value, name: true };
  try {
    await baileysChannel.updateIdentityName(props.inbox.id, newName.value);
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.NAME_SAVED'));
    editingName.value = false;
    await fetchIdentity();
  } catch (err) {
    useAlert(
      err.response?.data?.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.NAME_FAILED')
    );
  } finally {
    isLoading.value = { ...isLoading.value, name: false };
  }
};

const saveStatus = async () => {
  isLoading.value = { ...isLoading.value, status: true };
  try {
    await baileysChannel.updateIdentityStatus(props.inbox.id, newStatus.value);
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.STATUS_SAVED'));
    editingStatus.value = false;
    await fetchIdentity();
  } catch (err) {
    useAlert(
      err.response?.data?.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.STATUS_FAILED')
    );
  } finally {
    isLoading.value = { ...isLoading.value, status: false };
  }
};

const savePicture = async () => {
  if (!newPictureUrl.value.startsWith('http')) {
    useAlert(
      t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.PICTURE_INVALID_URL')
    );
    return;
  }
  isLoading.value = { ...isLoading.value, picture: true };
  try {
    await baileysChannel.updateIdentityPicture(
      props.inbox.id,
      newPictureUrl.value
    );
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.PICTURE_SAVED'));
    showPictureModal.value = false;
    newPictureUrl.value = '';
    await fetchIdentity();
  } catch (err) {
    useAlert(
      err.response?.data?.message ||
        t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.PICTURE_FAILED')
    );
  } finally {
    isLoading.value = { ...isLoading.value, picture: false };
  }
};

onMounted(fetchIdentity);
</script>

<template>
  <div class="p-4 rounded-lg border border-n-weak bg-n-background">
    <div class="flex items-center justify-between mb-3">
      <h4 class="text-sm font-semibold text-n-slate-12">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.TITLE') }}
      </h4>
      <NextButton ghost icon="i-lucide-refresh-cw" sm @click="fetchIdentity" />
    </div>

    <div class="flex items-center gap-4 mb-4">
      <img
        v-if="identity.picture_url"
        :src="identity.picture_url"
        alt="Profile"
        class="w-16 h-16 rounded-full object-cover ring-1 ring-n-weak"
      />
      <div
        v-else
        class="w-16 h-16 rounded-full bg-n-slate-3 flex items-center justify-center text-n-slate-11"
      >
        <span class="i-lucide-user size-6" />
      </div>
      <div class="flex-1 min-w-0">
        <div class="text-xs text-n-slate-11 uppercase tracking-wider">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.PHONE') }}
        </div>
        <div class="font-mono text-sm text-n-slate-12">
          {{ identity.phone || '-' }}
        </div>
      </div>
      <NextButton
        v-if="canEdit"
        ghost
        icon="i-lucide-image"
        sm
        @click="showPictureModal = true"
      >
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.CHANGE_PICTURE') }}
      </NextButton>
    </div>

    <!-- Name -->
    <div class="mb-3">
      <div class="text-xs text-n-slate-11 uppercase tracking-wider mb-1">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.NAME') }}
      </div>
      <div v-if="!editingName" class="flex items-center justify-between">
        <span class="text-sm text-n-slate-12">{{ identity.name || '-' }}</span>
        <NextButton
          v-if="canEdit"
          ghost
          icon="i-lucide-pencil"
          xs
          @click="editingName = true"
        />
      </div>
      <div v-else class="flex items-center gap-2">
        <input
          v-model="newName"
          type="text"
          maxlength="25"
          class="flex-1 rounded-md border-0 px-3 py-1.5 ring-1 ring-inset ring-n-strong text-n-slate-12 focus:ring-2 focus:ring-inset focus:ring-n-blue-9 sm:text-sm bg-n-background outline-none"
        />
        <NextButton
          ghost
          icon="i-lucide-x"
          sm
          @click="
            editingName = false;
            newName = identity.name || '';
          "
        />
        <NextButton
          blue
          icon="i-lucide-check"
          sm
          :is-loading="isLoading.name"
          @click="saveName"
        />
      </div>
    </div>

    <!-- Status / Recado -->
    <div>
      <div class="text-xs text-n-slate-11 uppercase tracking-wider mb-1">
        {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.STATUS') }}
      </div>
      <div v-if="!editingStatus" class="flex items-center justify-between">
        <span class="text-sm text-n-slate-12 italic">{{
          identity.status ||
          $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.NO_STATUS')
        }}</span>
        <NextButton
          v-if="canEdit"
          ghost
          icon="i-lucide-pencil"
          xs
          @click="editingStatus = true"
        />
      </div>
      <div v-else class="flex items-center gap-2">
        <input
          v-model="newStatus"
          type="text"
          maxlength="139"
          class="flex-1 rounded-md border-0 px-3 py-1.5 ring-1 ring-inset ring-n-strong text-n-slate-12 focus:ring-2 focus:ring-inset focus:ring-n-blue-9 sm:text-sm bg-n-background outline-none"
        />
        <NextButton
          ghost
          icon="i-lucide-x"
          sm
          @click="
            editingStatus = false;
            newStatus = identity.status || '';
          "
        />
        <NextButton
          blue
          icon="i-lucide-check"
          sm
          :is-loading="isLoading.status"
          @click="saveStatus"
        />
      </div>
    </div>

    <!-- Picture modal -->
    <div
      v-if="showPictureModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div
        class="bg-n-background rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl"
      >
        <h3 class="text-lg font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.PICTURE_TITLE') }}
        </h3>
        <p class="mt-2 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.PICTURE_BODY') }}
        </p>
        <input
          v-model="newPictureUrl"
          type="url"
          placeholder="https://..."
          class="mt-3 block w-full rounded-md border-0 px-3 py-2 ring-1 ring-inset ring-n-strong text-n-slate-12 focus:ring-2 focus:ring-inset focus:ring-n-blue-9 sm:text-sm bg-n-background outline-none font-mono"
        />
        <div class="flex justify-end gap-2 mt-5">
          <NextButton
            ghost
            @click="
              showPictureModal = false;
              newPictureUrl = '';
            "
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.CANCEL_BUTTON') }}
          </NextButton>
          <NextButton
            blue
            :is-loading="isLoading.picture"
            :disabled="!newPictureUrl.startsWith('http')"
            @click="savePicture"
          >
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.BAILEYS.IDENTITY.PICTURE_SAVE') }}
          </NextButton>
        </div>
      </div>
    </div>
  </div>
</template>
